import bundledLanguages from '@locales/list_languages.json';
import { env } from 'core/constants/env.constants';
import { setSupportedLanguages } from 'core/i18n/language.utils';

export interface SupportedLanguage {
  value: string;
  text: string;
}

// The app blocks on this catalog, so a stalled request would hold the splash
// screen open. Bound it: the abort lands in the catch and serves the bundle.
const LANGUAGE_CATALOG_TIMEOUT_MS = 5_000;

// Plain fetch, not the core axios instance: axios injects gateway auth headers
// that must not go to the S3 bucket.
export const getSupportedLanguages = async (): Promise<SupportedLanguage[]> => {
  try {
    const response = await fetch(`${env.VITE_LANGUAGE_API}/list_languages.json`, {
      cache: 'no-cache',
      signal: AbortSignal.timeout(LANGUAGE_CATALOG_TIMEOUT_MS),
    });

    if (!response.ok) {
      throw new Error(`Failed to load supported languages: ${response.status}`);
    }

    const data: unknown = await response.json();
    if (!isNonEmptyLanguageList(data)) {
      throw new Error('Unexpected or empty supported-languages payload');
    }

    setSupportedLanguages(data.map(language => language.value));
    return data;
  } catch {
    const fallback = bundledLanguages as SupportedLanguage[];
    setSupportedLanguages(fallback.map(language => language.value));
    return fallback;
  }
};

const isNonEmptyLanguageList = (data: unknown): data is SupportedLanguage[] =>
  Array.isArray(data) &&
  data.length > 0 &&
  data.every(item => typeof item?.value === 'string' && typeof item?.text === 'string');
